import React, {
	ChangeEvent,
	Dispatch,
	useCallback,
	useEffect,
	useState,
} from "react";
import { Select, SearchSelectOptionProps } from "@alm/flow-ds/components";
import SearchSelectAsyncPaginate from "elements/Lookup/SearchSelectAsyncPaginate";
import { Actions, createFilter, setQueryFilter } from "./finderReducer";
import {
	Clause,
	LookupElementDefinition,
	operators,
	QueryStatement,
} from "lib/api/types";
import useSearchSelect from "../../hooks/useSearchSelect";
import { useTranslation } from "react-i18next";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import _ from "lodash";

type Props = LookupElementDefinition & {
	index: number;
	dispatch: Dispatch<Actions>;
	queryValue?: string;
	queryOpts?: QueryStatement | Clause;
};

enum CustomOps {
	oneOf = "oneOf",
	allOf = "allOf",
	exact = "exact",
}

function LookupSearchField({
	index,
	label,
	id,
	dispatch,
	search,
	isArray,
	limit,
	queryValue,
	queryOpts,
}: Props) {
	const { t } = useTranslation();
	const [operator, setOperator] = useState<CustomOps>(
		isArray ? CustomOps.oneOf : CustomOps.exact
	);
	const { loadPageOptions, onChange, value } = useSearchSelect(search);

	const onOperatorChange = useCallback(
		(event: ChangeEvent<HTMLSelectElement>) => {
			setOperator(event.currentTarget.value as CustomOps);
		},
		[]
	);

	useEffect(() => {
		if (isArray && Array.isArray(value)) {
			const clause = operator === CustomOps.oneOf ? "OR" : "AND";
			const filters = value.map(v =>
				createFilter(jsonpointer.pointerToId(id), operators.exact, {
					value: v?.value,
				})
			);

			const filter: Clause = {
				[clause]: filters,
			};

			const summary = value
				.map(v => v.label)
				.join(clause === "OR" ? "|" : "&&");

			// Add to our query
			dispatch(setQueryFilter(filter, index, summary));
			// Add to our query summary
		} else {
			const v = value as SearchSelectOptionProps;
			createFilter(jsonpointer.pointerToId(id), operators.exact, {
				value: v?.value,
			});
			const summary = v?.label;

			dispatch(
				setQueryFilter(
					createFilter(jsonpointer.pointerToId(id), operators.exact, {
						value: v?.value,
					}),
					index,
					summary
				)
			);
		}
	}, [dispatch, id, index, isArray, operator, value]);

	useEffect(() => {
		// Check that there is a query value and queryOtps, if so repopulate data value
		if (queryValue !== undefined && queryOpts !== undefined) {
			// Check that it isn't an array lookup and that AND, NOT and OR aren't used accidentally
			if (
				!isArray &&
				queryOpts.AND === undefined &&
				queryOpts.NOT === undefined &&
				queryOpts.OR === undefined
			) {
				const newValue = { value: "", label: "" };
				// Set value to be value taken from queryOpts
				newValue.value =
					queryOpts[Object.keys(queryOpts)[0]].exact.value;
				// Set label as queryValue, with check incase it has sent as an array
				newValue.label = _.isArray(queryValue)
					? queryValue[0]
					: queryValue;
				onChange(newValue);
			}
			// Check that it is an array and that either AND or OR are used
			if (
				isArray &&
				(queryOpts.AND !== undefined || queryOpts.OR !== undefined)
			) {
				const newArray: SearchSelectOptionProps[] = [];
				const queryOptsArray =
					queryOpts[queryOpts.AND !== undefined ? "AND" : "OR"];
				// Split queryValue to get an array of values for the labels
				const splitValue = (
					_.isArray(queryValue) ? queryValue[0] : queryValue
				).split(queryOpts.AND !== undefined ? "&&" : "|");

				if (_.isArray(queryOptsArray)) {
					// Look through queryOptsArray setting the value and label for each and push to newArray
					queryOptsArray.forEach((opts, index) => {
						const newValue = { value: "", label: "" };
						newValue.value = opts[Object.keys(opts)[0]].exact.value;
						newValue.label = splitValue[index];
						newArray.push(newValue);
					});
					onChange(newArray);
				}
				// Set operator value using queryOpts
				if (queryOpts.AND !== undefined) {
					setOperator("allOf" as CustomOps);
				} else if (queryOpts.OR !== undefined) {
					setOperator("oneOf" as CustomOps);
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<div className="col-6 col-md-2">
				<Select
					label={t("elements.finder.selectOperatorLabel")}
					labelHidden={true}
					value={operator}
					disabled={!isArray}
					onChange={onOperatorChange}>
					{isArray ? (
						<>
							<option value={CustomOps.oneOf}>
								{t("elements.finder.oneOf")}
							</option>
							<option value={CustomOps.allOf}>
								{t("elements.finder.allOf")}
							</option>
						</>
					) : (
						<option value={CustomOps.exact}>
							{t("elements.finder.exact")}
						</option>
					)}
				</Select>
			</div>
			<div className="col-12 col-md-5 col-lg-6 col-xl-4 mt-3 mt-md-0">
				<SearchSelectAsyncPaginate
					labelHidden={true}
					onChange={onChange}
					label={label}
					loadPageOptions={loadPageOptions}
					debounceTimeout={400}
					clearable={true}
					multiselect={isArray}
					value={value}
					limit={
						limit && isArray
							? t("main.text.limit", { limit })
							: undefined
					}
				/>
			</div>
		</>
	);
}

export default LookupSearchField;
